import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { usePortfolio } from "@/context/PortfolioContext";
import { usePrivacy } from "@/context/PrivacyContext";
import { SubPortfolioSelect } from "@/components/SubPortfolioSelect";
import { HoldingsNotices } from "@/components/HoldingsNotices";
import { TickerWarningBadge } from "@/components/TickerWarningBadge";
import { EmptyState } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fmtCurrency, fmtPct, fmtNumber, fmtSigned, gainClass } from "@/lib/format";
import { PortfolioStrip } from "@/components/dashboard/PortfolioStrip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useMarketColors, dayClass } from "@/lib/marketHours";
import { Wallet, ArrowUpDown, ChevronUp, ChevronDown, Download, SlidersHorizontal, GripVertical, RefreshCw, X, Check, Lock, Bell, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const num = (v, d = 2) => fmtNumber(v, d);

const COLS = [
    { key: "ticker", label: "Symbol", align: "left", sort: "ticker", sticky: true,
      render: (h) => <span className="font-semibold num inline-flex items-center gap-1.5">{h.ticker}<TickerWarningBadge ticker={h.ticker} warning={h.ticker_warning} /></span> },
    { key: "shares", label: "Shares", align: "right", sort: "shares",
      render: (h) => <span className="num">{num(h.shares)}</span> },
    { key: "price", label: "Market Price", align: "right", sort: "current_price",
      render: (h) => <span className="num">{h.current_price != null ? fmtCurrency(h.current_price, { decimals: 2 }) : "—"}</span> },
    { key: "avg", label: "Avg Cost", align: "right", sort: "avg_cost",
      render: (h) => <span className="num">{fmtCurrency(h.avg_cost, { decimals: 2 })}</span> },
    { key: "daily", label: "Daily $", align: "right", sort: "day_change",
      render: (h, mask, on) => h.day_change != null ? (
          <span className={`num ${dayClass(h.day_change, on)}`}>{mask(fmtSigned(h.day_change))}</span>) : "—" },
    { key: "dailypct", label: "Daily %", align: "right", sort: "day_change_pct",
      render: (h, mask, on) => h.day_change_pct != null ? (
          <span className={`num ${dayClass(h.day_change_pct, on)}`}>{fmtPct(h.day_change_pct)}</span>) : "—" },
    { key: "mv", label: "Market Value", align: "right", sort: "market_value",
      render: (h, mask) => <span className="num font-medium">{h.market_value != null ? mask(fmtCurrency(h.market_value, { decimals: 0 })) : "—"}</span> },
    { key: "upl", label: "Unrealized P/L", align: "right", sort: "unrealized_pl",
      render: (h, mask) => <span className={`num ${gainClass(h.unrealized_pl)}`}>{h.unrealized_pl != null ? mask(fmtSigned(h.unrealized_pl, { decimals: 0 })) : "—"}</span> },
    { key: "plpct", label: "P/L %", align: "right", sort: "unrealized_pl_pct",
      render: (h) => <span className={`num ${gainClass(h.unrealized_pl_pct)}`}>{fmtPct(h.unrealized_pl_pct)}</span> },
    { key: "yoc", label: "Yield on Cost", align: "right", sort: "yield_on_cost",
      render: (h, mask) => <span className="num" title={h.yield_on_cost ? `Dividends received last 12 months: ${mask(fmtCurrency(h.ttm_dividends, { decimals: 2 }))} ÷ cost basis` : undefined}>{h.yield_on_cost ? `${h.yield_on_cost.toFixed(2)}%` : "—"}</span> },
    { key: "curyield", label: "Current Yield", align: "right", sort: "current_yield",
      render: (h, mask) => <span className="num text-muted-foreground" title={h.current_yield ? `Dividends received last 12 months: ${mask(fmtCurrency(h.ttm_dividends, { decimals: 2 }))} ÷ market value` : undefined}>{h.current_yield ? `${h.current_yield.toFixed(2)}%` : "—"}</span> },
    { key: "vol", label: "Hist Vol", align: "right", sort: "volatility",
      render: (h) => <span className="num text-muted-foreground">{h.volatility != null ? `${h.volatility.toFixed(1)}%` : "—"}</span> },
    { key: "sharpe", label: "Sharpe", align: "right", sort: "sharpe",
      render: (h) => <span className={`num ${gainClass(h.sharpe)}`}>{h.sharpe != null ? h.sharpe.toFixed(2) : "—"}</span> },
    { key: "iv", label: "Impl Vol", align: "right", sort: "implied_volatility",
      render: (h) => <span className="num text-muted-foreground">{h.implied_volatility != null ? `${h.implied_volatility.toFixed(1)}%` : "N/A"}</span> },
    { key: "portpct", label: "Port %", align: "right", sort: "portfolio_pct",
      render: (h) => <span className="num text-muted-foreground">{h.portfolio_pct?.toFixed(1)}%</span> },
    { key: "subpct", label: "Sub %", align: "right", sort: "sub_portfolio_pct",
      render: (h) => <span className="num text-muted-foreground">{h.sub_portfolio_pct?.toFixed(1)}%</span> },
    { key: "sub", label: "Sub-Portfolio", align: "left", sort: "sub_portfolio",
      render: (h) => <Badge variant="secondary" className="text-[10px]">{h.sub_portfolio}</Badge> },
    { key: "name", label: "Company", align: "left", sort: "name",
      render: (h) => <span className="text-muted-foreground block max-w-[160px] truncate" title={h.name}>{h.name}</span> },
];

const COL_MAP = Object.fromEntries(COLS.map((c) => [c.key, c]));
const LOCKED = "ticker"; // always first + visible (frozen column)
const DEFAULT_ORDER = COLS.map((c) => c.key);
const STORAGE_KEY = "apex_holdings_cols";

function loadColState() {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { saved = null; }
    const order = [LOCKED, ...(Array.isArray(saved?.order) ? saved.order.filter((k) => COL_MAP[k] && k !== LOCKED) : []).filter((k) => k !== LOCKED)];
    for (const k of DEFAULT_ORDER) {
        if (order.includes(k)) continue;
        const prev = DEFAULT_ORDER[DEFAULT_ORDER.indexOf(k) - 1]; // new columns slot in next to their default neighbour
        const at = order.indexOf(prev);
        at === -1 ? order.push(k) : order.splice(at + 1, 0, k);
    }
    // keep the split daily columns adjacent (dailypct right after daily)
    const di = order.indexOf("daily"), pi = order.indexOf("dailypct");
    if (di !== -1 && pi !== -1 && pi !== di + 1) { order.splice(pi, 1); order.splice(order.indexOf("daily") + 1, 0, "dailypct"); }
    const hidden = Array.isArray(saved?.hidden) ? saved.hidden.filter((k) => COL_MAP[k] && k !== LOCKED) : [];
    const widths = (saved && typeof saved.widths === "object" && saved.widths) ? saved.widths : {};
    return { order, hidden, widths };
}

const RATING_FIELDS = [
    { key: "sell_side_rating", label: "Wall St", live: true },
    { key: "quant_rating", label: "Quant" },
    { key: "authors_rating_pro", label: "SA Analysts" },
    { key: "value_category", label: "Value" },
    { key: "growth_category", label: "Growth" },
    { key: "profitability_category", label: "Profit." },
    { key: "momentum_category", label: "Momentum" },
    { key: "eps_revisions_category", label: "EPS Rev." },
];
const GRADE_FIELDS = ["value_category", "growth_category", "profitability_category", "momentum_category", "eps_revisions_category"];

function quantLabel(v) {
    if (v == null) return { text: "—", cls: "bg-muted text-muted-foreground" };
    const n = Number(v);
    if (n >= 4.5) return { text: `Strong Buy ${n.toFixed(2)}`, cls: "bg-gain/20 text-gain" };
    if (n >= 3.5) return { text: `Buy ${n.toFixed(2)}`, cls: "bg-gain/15 text-gain" };
    if (n >= 2.5) return { text: `Hold ${n.toFixed(2)}`, cls: "bg-amber-500/20 text-amber-500" };
    if (n >= 1.5) return { text: `Sell ${n.toFixed(2)}`, cls: "bg-loss/15 text-loss" };
    return { text: `Strong Sell ${n.toFixed(2)}`, cls: "bg-loss/20 text-loss" };
}
function gradeCls(g) {
    if (!g) return "text-muted-foreground";
    const c = String(g)[0].toUpperCase();
    if (c === "A") return "text-gain font-semibold";
    if (c === "B") return "text-emerald-400 font-semibold";
    if (c === "C") return "text-amber-500 font-semibold";
    if (c === "D") return "text-orange-500 font-semibold";
    return "text-loss font-semibold";
}
function avgCls(v) {
    if (v == null) return "text-muted-foreground";
    const n = Number(v);
    return n >= 3.5 ? "text-gain" : n >= 2.5 ? "text-amber-500" : "text-loss";
}

function gradeToNum(g) {
    if (!g) return null;
    const c = String(g)[0].toUpperCase();
    const base = { A: 5, B: 4, C: 3, D: 2, E: 1, F: 0 }[c];
    if (base == null) return null;
    const mod = String(g).includes("+") ? 0.3 : String(g).includes("-") ? -0.3 : 0;
    return base + mod;
}
function sortVal(item, field) {
    if (field === "ticker") return item.ticker || "";
    const raw = item[field];
    if (raw == null) return null;
    if (GRADE_FIELDS.includes(field)) return gradeToNum(raw);
    const n = Number(raw);
    return isNaN(n) ? null : n;
}

function LockedTag() {
    return <span className="inline-flex items-center gap-1 text-muted-foreground/50 text-xs" title="Premium — requires an unlocked Seeking Alpha data source"><Lock className="w-3 h-3" />Premium</span>;
}
function ratingCell(r, f) {
    const v = r[f.key];
    if (f.key === "quant_rating") {
        if (v == null) return <LockedTag />;
        const ql = quantLabel(v);
        return <span className={`inline-block px-2 py-0.5 rounded-full text-xs num ${ql.cls}`}>{ql.text}</span>;
    }
    if (GRADE_FIELDS.includes(f.key)) {
        return v ? <span className={`num ${gradeCls(v)}`}>{v}</span> : (f.live ? <span className="text-muted-foreground">—</span> : <LockedTag />);
    }
    if (v != null) return <span className={`num ${avgCls(v)}`}>{Number(v).toFixed(2)}</span>;
    return f.live ? <span className="text-muted-foreground">—</span> : <LockedTag />;
}

function RatingsView({ sub }) {
    const qc = useQueryClient();
    const [busy, setBusy] = useState(false);
    const [sort, setSort] = useState({ field: "sell_side_rating", dir: "desc" });
    const q = useQuery({ queryKey: ["ratings", sub], queryFn: async () => (await api.get(`/ratings?sub=${sub}`)).data });
    const items = q.data?.items || [];
    const fetchedAt = items.map((i) => i.fetched_at).filter(Boolean).sort().slice(-1)[0];

    const sorted = [...items].sort((a, b) => {
        const av = sortVal(a, sort.field), bv = sortVal(b, sort.field);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;   // nulls always last
        if (bv == null) return -1;
        let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        return sort.dir === "asc" ? cmp : -cmp;
    });

    const toggleSort = (field) => {
        setSort((s) => s.field === field
            ? { field, dir: s.dir === "asc" ? "desc" : "asc" }
            : { field, dir: field === "ticker" ? "asc" : "desc" });
    };
    const SortHead = ({ field, label, locked, className = "" }) => (
        <th className={`px-3 py-2 cursor-pointer select-none hover:text-foreground transition-colors ${className}`} onClick={() => toggleSort(field)} data-testid={`ratings-sort-${field}`}>
            <span className="inline-flex items-center gap-1">{label}{locked && <Lock className="w-3 h-3 opacity-50" />}{sort.field === field && <ArrowUpDown className={`w-3 h-3 ${sort.dir === "asc" ? "rotate-180" : ""}`} />}</span>
        </th>
    );

    const refresh = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ratings/refresh", { sub });
            toast.success(`Refreshed ratings for ${data.fetched} securities`);
            qc.invalidateQueries({ queryKey: ["ratings", sub] });
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    return (
        <Card className="overflow-hidden" data-testid="ratings-view">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-border">
                <div>
                    <div className="text-sm font-medium">Seeking Alpha Ratings {sub !== "ALL" ? `· ${sub}` : ""}</div>
                    <div className="text-xs text-muted-foreground">{fetchedAt ? `As of ${new Date(fetchedAt).toLocaleString()}` : "Not yet fetched — click Refresh to pull ratings"}{q.data && !q.data.configured ? " · API key not configured" : ""}</div>
                </div>
                <Button onClick={refresh} disabled={busy || q.data?.configured === false} data-testid="ratings-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Refresh ratings
                </Button>
            </div>
            {q.data?.configured === false && (
                <div className="p-4 text-sm text-amber-500" data-testid="ratings-not-configured">Seeking Alpha API key is not configured yet. An admin can add it in Settings → Integrations, then click Refresh to load ratings.</div>
            )}
            {q.data?.configured && (
                <div className="px-4 py-2 text-xs text-muted-foreground bg-muted/40 border-b border-border flex items-start gap-2" data-testid="ratings-premium-note">
                    <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span><span className="font-medium text-foreground">Wall St</span> analyst rating is live. Quant Rating &amp; Factor Grades are <span className="font-medium">premium-locked</span> at the data source — they'll fill in automatically once an unlocked Seeking Alpha source is configured.</span>
                </div>
            )}
            <div className="overflow-auto max-h-[calc(100vh-16rem)]">
                <table className="w-full text-sm" data-testid="ratings-table">
                    <thead className="border-b border-border">
                        <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            <SortHead field="ticker" label="Symbol" className="text-left sticky left-0 bg-card z-10" />
                            {RATING_FIELDS.map((f) => <SortHead key={f.key} field={f.key} label={f.label} locked={!f.live} className="text-center" />)}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((r) => (
                            <tr key={r.ticker} className="border-b border-border/50 hover:bg-muted/40" data-testid={`ratings-row-${r.ticker}`}>
                                <td className="px-3 py-3 text-left font-semibold num sticky left-0 bg-card">{r.ticker}</td>
                                {RATING_FIELDS.map((f) => <td key={f.key} className="px-3 py-3 text-center">{ratingCell(r, f)}</td>)}
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground" data-testid="ratings-empty">No holdings in this sub-portfolio.</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">Wall St &amp; Quant are 1–5 averages · Factor Grades A–F · Click a column to sort · Source: Seeking Alpha (via RapidAPI) · Refreshes only this sub-portfolio on demand</div>
        </Card>
    );
}

// Totals for the rows currently shown (respects sub-portfolio and search)
function holdingsStripCells(list, mask, dayOn) {
    const sum = (k) => list.reduce((t, h) => t + (h[k] || 0), 0);
    const mv = sum("market_value"), day = sum("day_change"), cost = sum("cost_basis"), pl = sum("unrealized_pl"), div = sum("ttm_dividends");
    const prev = mv - day;
    const priced = list.some((h) => h.market_value != null);
    const m = (v, f = fmtCurrency) => (priced ? mask(f(v)) : "—"); // no prices yet (cold start) -> dashes, not $0
    return [
        { testId: "hstat-value", label: "Market Value", value: m(mv), sub: `${list.length} positions` },
        { testId: "hstat-today", label: "Today", value: m(day, fmtSigned), valueClass: priced ? dayClass(day, dayOn) : "", dayOn, delta: day, deltaPct: priced && prev ? fmtPct((day / prev) * 100) : "" },
        { testId: "hstat-unrealized", label: "Unrealized P/L", value: m(pl, fmtSigned), valueClass: priced ? gainClass(pl) : "", delta: pl, deltaPct: priced && cost ? fmtPct((pl / cost) * 100) : "" },
        { testId: "hstat-cost", label: "Cost Basis", value: mask(fmtCurrency(cost)) },
        { testId: "hstat-dividends", label: "Dividends · 12m", value: mask(fmtCurrency(div)), sub: cost ? `${((div / cost) * 100).toFixed(2)}% yield on cost` : undefined },
    ];
}

export default function Holdings() {
    const { selectedSub, setSelectedSub } = usePortfolio();
    const { mask } = usePrivacy();
    const [sort, setSort] = useState({ key: "market_value", dir: "desc" });
    const [colState, setColState] = useState(loadColState);
    const [dragKey, setDragKey] = useState(null);
    const [presets, setPresets] = useState(() => { try { return JSON.parse(localStorage.getItem("apex_holdings_presets")) || []; } catch { return []; } });
    const [presetName, setPresetName] = useState("");
    const [view, setView] = useState("holdings");
    const navigate = useNavigate();

    useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(colState)); }, [colState]);
    useEffect(() => { localStorage.setItem("apex_holdings_presets", JSON.stringify(presets)); }, [presets]);

    const savePreset = () => {
        const name = presetName.trim();
        if (!name) return;
        setPresets((p) => [...p.filter((x) => x.name !== name), { name, order: colState.order, hidden: colState.hidden, widths: colState.widths }]);
        setPresetName("");
    };
    const applyPreset = (p) => setColState({
        order: [LOCKED, ...p.order.filter((k) => COL_MAP[k] && k !== LOCKED)],
        hidden: (p.hidden || []).filter((k) => COL_MAP[k] && k !== LOCKED),
        widths: p.widths || {},
    });
    const deletePreset = (name) => setPresets((p) => p.filter((x) => x.name !== name));

    const visibleCols = colState.order.filter((k) => k === LOCKED || !colState.hidden.includes(k)).map((k) => COL_MAP[k]);

    const toggleCol = (k) => setColState((s) => ({
        ...s,
        hidden: s.hidden.includes(k) ? s.hidden.filter((x) => x !== k) : [...s.hidden, k],
    }));
    const reorderCol = (from, to) => setColState((s) => {
        if (from === to || from === LOCKED || to === LOCKED) return s;
        const order = s.order.filter((k) => k !== LOCKED);
        const fi = order.indexOf(from), ti = order.indexOf(to);
        if (fi < 0 || ti < 0) return s;
        order.splice(ti, 0, order.splice(fi, 1)[0]);
        return { ...s, order: [LOCKED, ...order] };
    });
    const resetCols = () => setColState({ order: [...DEFAULT_ORDER], hidden: [], widths: {} });

    const startResize = (e, key) => {
        e.stopPropagation();
        e.preventDefault();
        const startX = e.clientX;
        const th = e.currentTarget.closest("th");
        const startW = th.offsetWidth;
        const onMove = (ev) => {
            const w = Math.max(64, startW + (ev.clientX - startX));
            setColState((s) => ({ ...s, widths: { ...s.widths, [key]: w } }));
        };
        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            document.body.style.userSelect = "";
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
        document.body.style.userSelect = "none";
    };
    const autoFitCol = (key) => setColState((s) => {
        const widths = { ...s.widths };
        delete widths[key];
        return { ...s, widths };
    });
    const widthStyle = (key) => (colState.widths?.[key] ? { width: colState.widths[key], minWidth: colState.widths[key], maxWidth: colState.widths[key] } : undefined);

    const { data, isLoading, isFetching, dataUpdatedAt } = useQuery({ queryKey: ["holdings-page", selectedSub], queryFn: async () => (await api.get(`/portfolio/holdings?sub=${selectedSub}`)).data });
    const qc = useQueryClient();
    const refreshData = async () => { try { await api.post("/market/refresh"); } catch { /* fall back to cache */ } qc.invalidateQueries(); };
    const [search, setSearch] = useState("");
    const params = useQuery({ queryKey: ["parameters"], queryFn: async () => (await api.get("/parameters")).data });
    const inflation = params.data?.inflation_rate ?? 4;
    const notesQ = useQuery({ queryKey: ["notes"], queryFn: async () => (await api.get("/notes")).data });
    const alertsQ = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts")).data });
    const noteMap = Object.fromEntries((notesQ.data || []).map((n) => [n.ticker, n.note]));
    const alertSet = new Set((alertsQ.data?.alerts || []).filter((a) => a.status !== "dismissed").map((a) => a.ticker));
    const noteInitials = (t) => { const n = noteMap[t]; return n ? (n.length > 18 ? n.slice(0, 18) + "…" : n) : ""; };

    const withSharpe = (data?.holdings || []).map((h) => ({
        ...h,
        sharpe: (h.volatility != null && h.one_year_return != null && Math.abs(h.one_year_return) > 0.01)
            ? (h.volatility - inflation) / h.one_year_return : null,
    }));
    const rows = [...withSharpe].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        if (typeof av === "string") return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        return sort.dir === "asc" ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });

    const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
    const q2 = search.trim().toLowerCase();
    const filtered = q2 ? rows.filter((h) => h.ticker.toLowerCase().includes(q2) || (h.name || "").toLowerCase().includes(q2)) : rows;

    const dayOn = useMarketColors();
    const exportCsv = async (view) => {
        const res = await api.get(`/holdings/export?view=${view}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = view === "consolidated" ? "holdings_consolidated.csv" : "holdings.csv"; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight">Holdings</h1>
                    <p className="text-sm text-muted-foreground" data-testid="holdings-updated">{data ? `${filtered.length}${q2 ? ` of ${rows.length}` : ""} positions · ${data.source} · updated ${new Date(dataUpdatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}` : "Loading…"}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {view === "holdings" && (
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticker…"
                                className="h-9 w-36 sm:w-44 pl-8 text-sm" data-testid="holdings-search" />
                        </div>
                    )}
                    <div className="inline-flex rounded-lg bg-muted p-0.5 mr-1" data-testid="holdings-view-toggle">
                        <button onClick={() => setView("holdings")} data-testid="view-holdings"
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === "holdings" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Holdings</button>
                        <button onClick={() => setView("ratings")} data-testid="view-ratings"
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === "ratings" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Ratings</button>
                    </div>
                    <SubPortfolioSelect value={selectedSub} onChange={setSelectedSub} />
                    {view === "holdings" && <Button variant="outline" size="icon" onClick={refreshData} disabled={isFetching} title="Refresh market data" data-testid="holdings-refresh-btn">
                        <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
                    </Button>}
                    {view === "holdings" && <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" data-testid="columns-btn"><SlidersHorizontal className="w-4 h-4 mr-1.5" />Columns</Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72 p-0" data-testid="columns-popover">
                            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Columns</span>
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={resetCols} data-testid="columns-reset">Reset</Button>
                            </div>
                            <ScrollArea className="max-h-72">
                                <div className="p-1.5">
                                    {colState.order.map((k) => {
                                        const c = COL_MAP[k];
                                        const locked = k === LOCKED;
                                        const shown = locked || !colState.hidden.includes(k);
                                        return (
                                            <div key={k}
                                                draggable={!locked}
                                                onDragStart={() => !locked && setDragKey(k)}
                                                onDragOver={(e) => { e.preventDefault(); }}
                                                onDrop={(e) => { e.preventDefault(); if (dragKey && !locked) reorderCol(dragKey, k); setDragKey(null); }}
                                                onDragEnd={() => setDragKey(null)}
                                                className={`flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 ${locked ? "" : "cursor-grab active:cursor-grabbing"} ${dragKey === k ? "opacity-50" : ""}`}
                                                data-testid={`col-item-${k}`}>
                                                <GripVertical className={`w-3.5 h-3.5 shrink-0 ${locked ? "opacity-20" : "text-muted-foreground"}`} />
                                                <Checkbox checked={shown} disabled={locked} onCheckedChange={() => toggleCol(k)} data-testid={`col-toggle-${k}`} />
                                                <span className={`flex-1 text-sm ${shown ? "" : "text-muted-foreground"}`}>{c.label}{locked && <span className="text-[10px] text-muted-foreground ml-1">(frozen)</span>}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                            <div className="px-3 py-2 border-t border-border space-y-2" data-testid="col-presets">
                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Presets</div>
                                {presets.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {presets.map((p) => (
                                            <span key={p.name} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-secondary text-xs" data-testid={`preset-${p.name}`}>
                                                <button className="hover:text-primary" onClick={() => applyPreset(p)} data-testid={`preset-apply-${p.name}`}>{p.name}</button>
                                                <button className="text-muted-foreground hover:text-loss" onClick={() => deletePreset(p.name)} data-testid={`preset-delete-${p.name}`}><X className="w-3 h-3" /></button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-1.5">
                                    <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && savePreset()}
                                        placeholder="Save current as…" className="h-8 text-xs" data-testid="preset-name-input" />
                                    <Button size="icon" className="h-8 w-8 shrink-0" onClick={savePreset} disabled={!presetName.trim()} data-testid="preset-save-btn"><Check className="w-4 h-4" /></Button>
                                </div>
                            </div>
                            <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground">Drag rows to reorder · drag column edges in the table to resize</div>
                        </PopoverContent>
                    </Popover>}
                    {view === "holdings" && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="outline" data-testid="export-holdings-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => exportCsv("detailed")} data-testid="export-detailed">By sub-portfolio (one row per holding)</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportCsv("consolidated")} data-testid="export-consolidated">Consolidated (one row per stock)</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>

            {view === "holdings" && <HoldingsNotices />}
            {view === "holdings" && rows.length > 0 && <PortfolioStrip cells={holdingsStripCells(filtered, mask, dayOn)} />}

            {view === "ratings" ? <RatingsView sub={selectedSub} /> : (
            isLoading ? <Skeleton className="h-96 rounded-xl" /> : rows.length === 0 ? (
                <Card><EmptyState icon={Wallet} title="No holdings yet" description="Buy transactions will appear here as positions." testId="holdings-empty" /></Card>
            ) : (
                <>
                    {/* Desktop table */}
                    <Card className="hidden md:block overflow-hidden">
                        <div className="overflow-auto max-h-[calc(100vh-14rem)]">
                            <table className="w-auto min-w-full text-[13px]" data-testid="holdings-table">
                                <thead className="border-b border-border">
                                    <tr>
                                        {visibleCols.map((c) => (
                                            <th key={c.key} onClick={() => toggleSort(c.sort)} style={widthStyle(c.key)}
                                                className={`group/th relative px-2.5 py-1.5 text-[10px] uppercase tracking-wide font-semibold text-muted-foreground cursor-pointer hover:text-foreground whitespace-normal leading-tight align-bottom bg-card sticky top-0 ${c.align === "right" ? "text-right" : "text-left"} ${c.sticky ? "left-0 z-30" : "z-20"}`}>
                                                <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                                                    {c.label}{sort.key === c.sort
                                                        ? (sort.dir === "asc"
                                                            ? <ChevronUp className="w-3 h-3 shrink-0 text-primary" data-testid={`sort-asc-${c.key}`} />
                                                            : <ChevronDown className="w-3 h-3 shrink-0 text-primary" data-testid={`sort-desc-${c.key}`} />)
                                                        : <ArrowUpDown className="w-3 h-3 shrink-0 opacity-30" />}
                                                </span>
                                                <span onMouseDown={(e) => startResize(e, c.key)} onClick={(e) => e.stopPropagation()}
                                                    onDoubleClick={(e) => { e.stopPropagation(); autoFitCol(c.key); }}
                                                    title="Drag to resize · double-click to auto-fit"
                                                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/50 opacity-0 group-hover/th:opacity-100 transition-opacity"
                                                    data-testid={`col-resize-${c.key}`} />
                                            </th>
                                        ))}
                                        <th className="px-2.5 py-1.5 text-[10px] uppercase tracking-wide font-semibold text-muted-foreground text-left align-bottom bg-card sticky top-0 z-20 whitespace-nowrap">Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((h) => (
                                        <tr key={`${h.sub_portfolio}-${h.ticker}`} onClick={() => navigate(`/security/${h.ticker}`)}
                                            className="group border-b border-border/50 hover:bg-muted/40 cursor-pointer transition-colors" data-testid={`holdings-row-${h.ticker}`}>
                                            {visibleCols.map((c) => (
                                                <td key={c.key} style={widthStyle(c.key)} className={`px-2.5 py-1.5 whitespace-nowrap overflow-hidden text-ellipsis ${c.align === "right" ? "text-right" : "text-left"} ${c.sticky ? "sticky left-0 z-10 bg-card" : ""}`}>
                                                    {c.render(h, mask, dayOn)}
                                                </td>
                                            ))}
                                            <td className="px-2.5 py-1.5 text-left text-xs text-muted-foreground whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {alertSet.has(h.ticker) && <Bell className="w-3 h-3 text-primary shrink-0" data-testid={`holdings-alert-${h.ticker}`} />}
                                                    <span className="max-w-[160px] truncate inline-block align-bottom" title={noteMap[h.ticker] || ""} data-testid={`holdings-note-${h.ticker}`}>{noteInitials(h.ticker)}</span>
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-2">
                        {filtered.map((h) => (
                            <Card key={`${h.sub_portfolio}-${h.ticker}`} onClick={() => navigate(`/security/${h.ticker}`)} className="p-3" data-testid={`holdings-card-${h.ticker}`}>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-semibold num inline-flex items-center gap-1.5">{h.ticker}<TickerWarningBadge ticker={`${h.ticker}-m`} warning={h.ticker_warning} />{alertSet.has(h.ticker) && <Bell className="w-3 h-3 text-primary" />}</div>
                                        <div className="text-xs text-muted-foreground truncate max-w-[160px]">{h.name}</div>
                                        <Badge variant="secondary" className="text-[10px] mt-1">{h.sub_portfolio}</Badge>
                                        {noteMap[h.ticker] && <div className="text-[11px] text-muted-foreground mt-1 truncate max-w-[180px]">Note: {noteInitials(h.ticker)}</div>}
                                    </div>
                                    <div className="text-right">
                                        <div className="num font-semibold">{h.market_value != null ? mask(fmtCurrency(h.market_value, { decimals: 0 })) : "—"}</div>
                                        <div className={`num text-xs ${dayClass(h.day_change, dayOn)}`}>{mask(fmtSigned(h.day_change))} ({fmtPct(h.day_change_pct)})</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                                    <div><div className="text-muted-foreground">Shares</div><div className="num">{num(h.shares)}</div></div>
                                    <div><div className="text-muted-foreground">Price</div><div className="num">{h.current_price != null ? fmtCurrency(h.current_price, { decimals: 2 }) : "—"}</div></div>
                                    <div><div className="text-muted-foreground">Unreal.</div><div className={`num ${gainClass(h.unrealized_pl)}`}>{fmtPct(h.unrealized_pl_pct)}</div></div>
                                    <div><div className="text-muted-foreground">Port %</div><div className="num">{h.portfolio_pct?.toFixed(1)}%</div></div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </>
            ))}
        </div>
    );
}
